import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PoPageDynamicSearchComponent, PoPageDynamicSearchFilters, PoPageDynamicSearchLiterals } from "@po-ui/ng-templates";
import { HttpErrorResponse } from "@angular/common/http";
import {
  PoComboOption,
  PoDialogService,
  PoDisclaimer,
  PoModalAction,
  PoModalComponent,
  PoMultiselectFilterMode,
  PoNotificationService,
  PoPageAction,
  PoRadioGroupOption,
  PoSelectOption,
  PoStepperItem,
  PoTableAction,
  PoTableColumn,
} from "@po-ui/ng-components";
import { Observable, Subscription, forkJoin } from "rxjs";

import { Area, AreaReferences, AreaUsage } from "src/app/interfaces/area";
import { Tribe } from "src/app/interfaces/tribe";
import { AreaService } from "../services/area.service";
import { AreaComboFilter } from "../services/area-combo.filter";
import { TribeService } from "../services/tribe.service";
import { TransferUsersResult, UserTransferService } from "../services/user-transfer.service";
import { UserAccessItem, UsersAccessService } from "../services/users-access.service";
import { USERS_ADMIN_BASE } from "../area-tribe-admin.component";
import { asList, disclaimerValue, disclaimerValues, showFilterDisclaimers } from "../search-filters.util";
import { TableHeightFitter } from "../users-admin.utils";

/** Opção "Sem tribe" no filtro de Tribe. */
export const NO_TRIBE = "__none__";
type TransferMode = "transfer" | "delete";

// O Smart Review guarda so o id da area: apos a exclusao o historico dele perde o nome e os repositorios da area deixam de ser acessiveis a usuarios comuns
export const AREA_HISTORY_WARNING =
  "<strong>Atenção:</strong> se a área estiver em uso no Smart Review (repositórios, reviews ou custos), " +
  "depois da exclusão o nome some do histórico e os repositórios ficam inacessíveis para usuários comuns, " +
  "porque o Smart Review registra a área apenas pelo id. " +
  "Para aposentar uma área em uso, renomeie-a (ex.: <em>[Extinta] Nome</em>) e mova os usuários para a área nova.";

type AreaRow = Area & { tribeLabel: string };

// Coleções que guardam a área pelo nome (mesmas chaves do GET /area/:id/usage)
const REFERENCE_LABELS: Record<string, string> = { resources: "recurso(s)", hosts: "host(s)", spots: "spot(s)", recipes: "receita(s)" };

function describeReferences(references: AreaReferences, suffix = ""): Array<string> {
  return Object.entries(references ?? {})
    .filter(([, count]) => !!count)
    .map(([name, count]) => `${count} ${REFERENCE_LABELS[name] ?? name}${suffix}`);
}

@Component({
  selector: "app-structure",
  templateUrl: "./structure.component.html",
  styleUrls: ["./structure.component.css"],
})
export class StructureComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("areaModal", { static: true }) areaModal: PoModalComponent;
  @ViewChild("transferModal", { static: true }) transferModal: PoModalComponent;
  @ViewChild(PoPageDynamicSearchComponent, { static: true }) search: PoPageDynamicSearchComponent;

  loading = false;
  tribes: Array<Tribe> = [];
  areas: Array<Area> = [];
  // Filtros
  q = "";
  selectedTribes: Array<string> = [];

  searchFilters: Array<PoPageDynamicSearchFilters> = [];
  readonly searchLiterals: PoPageDynamicSearchLiterals = {
    searchPlaceholder: "Buscar área",
    filterTitle: "Busca avançada",
    filterConfirmLabel: "Aplicar",
    disclaimerGroupTitle: "Filtros ativos",
  };

  // Linhas exibidas
  areaRows: Array<AreaRow> = [];
  tribeComboOptions: Array<PoSelectOption> = [];

  readonly pageActions: Array<PoPageAction> = [
    { label: "Nova área", icon: "po-icon-plus", action: () => this.openAreaForm() },
    { label: "Atualizar", icon: "po-icon-refresh", action: () => this.load() },
  ];

  readonly areaColumns: Array<PoTableColumn> = [
    { property: "area", label: "Área", width: "50%", type: "link", action: (_: string, area: Area) => this.openAreaForm(area) },
    { property: "tribeLabel", label: "Tribe", width: "30%", type: "cellTemplate" },
    { property: "usersCount", label: "Usuários", type: "cellTemplate" },
  ];

  /** A tabela ocupa o que sobra da tela: a página não rola, só as linhas. */
  readonly tableFit: TableHeightFitter;

  readonly areaActions: Array<PoTableAction> = [
    { label: "Editar", icon: "po-icon-edit", action: (area: Area) => this.openAreaForm(area) },
    { label: "Ver usuários", icon: "po-icon-users", action: (area: Area) => this.showUsers({ area: area.id }) },
    { label: "Transferir usuários", icon: "po-icon-arrow-right", action: (area: Area) => this.openTransfer(area, "transfer"), disabled: (area: Area) => !area.usersCount },
    { label: "Excluir", icon: "po-icon-delete", type: "danger", action: (area: Area) => this.confirmRemoveArea(area) },
  ];


  // Formulário de tribe
  /** "Nova tribe" dentro do formulário de área (sem modal sobre modal). */
  creatingTribe = false;
  savingTribe = false;
  tribeName = "";
  tribeNameError = "";
  areaNameError = "";

  // Formulário de área
  editingArea: Area | null = null;
  areaName = "";
  areaTribeId = "";
  areaRestricted = false;
  savingForm = false;

  // Transferência (e exclusão) de área
  transferMode: TransferMode = "transfer";
  transferArea: Area | null = null;
  /** Só na exclusão: referências da área que vão para o destino junto com os usuários. */
  transferReferences: Array<string> = [];
  transferToAreaId = "";
  transferWho: "all" | "some" = "all";
  transferUsers: Array<UserAccessItem> = [];
  transferSelected: Array<string> = [];
  loadingTransferUsers = false;
  transferring = false;
  transferDestinationOptions: Array<PoComboOption> = [];
  readonly areaCombo = new AreaComboFilter();
  readonly historyWarning = AREA_HISTORY_WARNING;
  readonly transferSteps: Array<PoStepperItem> = [{ label: "Transferir" }, { label: "Excluir área" }];
  readonly transferWhoOptions: Array<PoRadioGroupOption> = [
    { label: "Todos os usuários da área", value: "all" },
    { label: "Escolher usuários", value: "some" },
  ];
  readonly transferUserColumns: Array<PoTableColumn> = [{ property: "username", label: "Usuário" }];

  areaPrimaryAction: PoModalAction = { label: "Salvar", action: () => this.saveArea(), disabled: true };
  transferPrimaryAction: PoModalAction = { label: "Transferir", action: () => this.confirmTransfer() };
  readonly cancelAction = (modal: () => PoModalComponent): PoModalAction => ({ label: "Cancelar", action: () => modal()?.close() });
  readonly areaCancel = this.cancelAction(() => this.areaModal);
  readonly transferCancel = this.cancelAction(() => this.transferModal);

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tribeService: TribeService,
    private areaService: AreaService,
    private userTransferService: UserTransferService,
    private usersAccessService: UsersAccessService,
    private poDialog: PoDialogService,
    private poNotification: PoNotificationService,
    elementRef: ElementRef<HTMLElement>,
    zone: NgZone,
  ) {
    this.tableFit = new TableHeightFitter(elementRef.nativeElement, zone);
  }

  ngOnInit(): void {
    // Atalhos: ?tribe=<id> (vindo de Tribes) ou ?situation=noTribe
    const params = this.route.snapshot.queryParamMap;
    this.selectedTribes = params.getAll("tribe");
    if (params.get("situation") === "noTribe") {
      this.selectedTribes = [...this.selectedTribes, NO_TRIBE];
    }
    this.load();
  }

  ngAfterViewInit(): void {
    this.tableFit.start();
  }

  ngOnDestroy(): void {
    this.tableFit.stop();
    this.subscriptions.unsubscribe();
  }

  // ── Dados ───────────────────────────────────────────────────────────────────

  load(): void {
    this.loading = true;
    this.subscriptions.add(
      forkJoin({ tribes: this.tribeService.list(), areas: this.areaService.list() }).subscribe({
        next: ({ tribes, areas }) => {
          this.tribes = tribes;
          this.areas = areas;
          this.buildSearchFilters();
          this.rebuild();
        },
        complete: () => (this.loading = false),
        error: () => (this.loading = false),
      }),
    );
  }

  private rebuild(): void {
    const tribeName = new Map(this.tribes.map((t) => [t.id, t.tribe]));
    const q = this.q.toLowerCase();
    const matches = (text: string) => !q || text.toLowerCase().includes(q);

    this.areaRows = this.areas
      .map((a) => ({ ...a, tribeLabel: (a.tribe && tribeName.get(a.tribe)) || "" }))
      .filter((a) => !this.selectedTribes.length || this.selectedTribes.includes(a.tribe || NO_TRIBE))
      .filter((a) => matches(a.area))
      .sort((a, b) => a.area.localeCompare(b.area));


    const sorted = this.tribes.slice().sort((a, b) => a.tribe.localeCompare(b.tribe));
    this.tribeComboOptions = sorted.map((t) => ({ value: t.id, label: t.tribe }));
  }

  // ── Filtros ─────────────────────────────────────────────────────────────────

  get noTribeCount(): number {
    return this.areas.filter((a) => !a.tribe).length;
  }

  private buildSearchFilters(): void {
    this.searchFilters = [
      {
        property: "tribe",
        label: "Tribe",
        optionsMulti: true,
        filterMode: PoMultiselectFilterMode.contains,
        gridColumns: 12,
        options: [
          { value: NO_TRIBE, label: `Sem tribe (${this.noTribeCount})` },
          ...this.tribes.slice().sort((a, b) => a.tribe.localeCompare(b.tribe)).map((t) => ({ value: t.id, label: t.tribe })),
        ],
      },
    ];
    if (this.selectedTribes.length && this.search) {
      this.syncing = true;
      showFilterDisclaimers(this.search, this.searchFilters, { tribe: this.selectedTribes }, this.q);
      this.syncing = false;
    }
  }

  private syncing = false;

  onQuickSearch(q: string): void {
    this.q = (q ?? "").trim();
    this.rebuild();
  }

  onAdvancedSearch(filter: Record<string, unknown> | null): void {
    if (this.syncing) {
      return;
    }
    // A busca avançada do PO troca os disclaimers e limpa o campo de busca rápida
    this.q = "";
    this.selectedTribes = asList(filter?.["tribe"]);
    this.rebuild();
  }

  onChangeDisclaimers(disclaimers: Array<PoDisclaimer>): void {
    this.q = disclaimerValue(disclaimers, "search") ?? "";
    this.selectedTribes = disclaimerValues(disclaimers, "tribe");
    this.rebuild();
  }

  showUsers(params: { area?: string; tribe?: string }): void {
    this.router.navigate([`${USERS_ADMIN_BASE}/users`], { queryParams: params });
  }

  // ── Tribe ───────────────────────────────────────────────────────────────────

  /** "Nova tribe" de dentro do formulário de área: troca o select por um campo e, ao criar, já seleciona. */
  openTribeForm(): void {
    this.tribeName = "";
    this.creatingTribe = true;
    this.validateTribeName();
    this.validateAreaForm();
  }

  cancelTribeForm(): void {
    const wasCreating = this.creatingTribe;
    this.creatingTribe = false;
    this.tribeName = "";
    this.tribeNameError = "";
    if (wasCreating) {
      this.validateAreaForm();
    }
  }

  onTribeNameChange(name: string): void {
    this.tribeName = name ?? "";
    this.validateTribeName();
  }

  get canCreateTribe(): boolean {
    return !!this.tribeName.trim() && !this.tribeNameError && !this.savingTribe;
  }

  private validateTribeName(): void {
    const name = this.tribeName.trim();
    const duplicate = this.tribes.some((t) => t.tribe.trim().toLowerCase() === name.toLowerCase());
    this.tribeNameError = duplicate ? `Já existe a tribe "${name}".` : "";
  }

  saveTribe(): void {
    if (!this.canCreateTribe) {
      return;
    }
    const tribe = this.tribeName.trim();
    this.savingTribe = true;
    this.subscriptions.add(
      this.tribeService.create({ tribe }).subscribe({
        next: (created) => {
          this.poNotification.success(`Tribe "${tribe}" criada.`);
          this.tribes = [...this.tribes, created];
          this.rebuild();
          this.areaTribeId = created.id;
          this.cancelTribeForm();
          this.validateAreaForm();
        },
        complete: () => (this.savingTribe = false),
        error: () => (this.savingTribe = false),
      }),
    );
  }

  // ── Área ────────────────────────────────────────────────────────────────────

  openAreaForm(area: Area | null = null): void {
    this.editingArea = area;
    this.areaName = area?.area ?? "";
    const presetTribe = this.selectedTribes.length === 1 && this.selectedTribes[0] !== NO_TRIBE ? this.selectedTribes[0] : "";
    this.areaTribeId = area ? area.tribe ?? "" : presetTribe;
    this.areaRestricted = area?.restricted ?? false;
    this.cancelTribeForm();
    this.validateAreaForm();
    this.areaModal?.open();
  }

  get renamingArea(): boolean {
    return !!this.editingArea && this.areaName.trim() !== this.editingArea.area;
  }

  /** Revalida a cada mudança do formulário; "Salvar" só habilita com nome único, tribe e alguma alteração. */
  validateAreaForm(): void {
    const name = this.areaName.trim();
    const duplicate = this.areas.some(
      (a) => a.id !== this.editingArea?.id && a.area.trim().toLowerCase() === name.toLowerCase(),
    );
    this.areaNameError = duplicate ? `Já existe a área "${name}".` : "";
    const unchanged =
      !!this.editingArea &&
      name === this.editingArea.area &&
      this.areaTribeId === (this.editingArea.tribe ?? "") &&
      this.areaRestricted === !!this.editingArea.restricted;
    this.areaPrimaryAction = { ...this.areaPrimaryAction, disabled: !name || duplicate || !this.areaTribeId || unchanged };
    if (this.creatingTribe) {
      this.areaPrimaryAction = { ...this.areaPrimaryAction, disabled: true };
    }
  }

  saveArea(): void {
    const area = this.areaName.trim();
    const payload = { area, restricted: this.areaRestricted, tribe: this.areaTribeId };
    const request: Observable<Area> = this.editingArea
      ? this.areaService.update(this.editingArea.id, payload)
      : this.areaService.create(payload);

    this.areaPrimaryAction = { ...this.areaPrimaryAction, loading: true };
    this.subscriptions.add(
      request.subscribe({
        next: () => {
          this.poNotification.success(this.editingArea ? `Área "${area}" atualizada.` : `Área "${area}" criada.`);
          this.areaModal?.close();
          this.load();
        },
        complete: () => (this.areaPrimaryAction = { ...this.areaPrimaryAction, loading: false }),
        error: () => (this.areaPrimaryAction = { ...this.areaPrimaryAction, loading: false }),
      }),
    );
  }

  /** Consulta o que ainda usa a área (v2 e ESP v1): sem nada, só confirma; com usuários ou referências, pede o destino. */
  confirmRemoveArea(area: Area): void {
    this.subscriptions.add(
      this.areaService.usage(area.id).subscribe((usage) => {
        const v1Users = usage.v1?.users ?? 0;
        if (v1Users && !usage.users) {
          this.poNotification.warning(`A área "${area.area}" tem ${v1Users} usuário(s) só no ESP v1. Mova-os para outra área no ESP v1 antes de excluí-la.`);
          return;
        }
        const references = [...describeReferences(usage.references), ...describeReferences(usage.v1?.references ?? {}, " no ESP v1")];
        if (usage.users || references.length) {
          this.openTransfer({ ...area, usersCount: usage.users }, "delete", references);
          return;
        }
        this.poDialog.confirm({
          title: "Excluir área",
          message: `Excluir a área <strong>${area.area}</strong>?<br><br>${AREA_HISTORY_WARNING}`,
          confirm: () => this.removeArea(area),
        });
      }),
    );
  }

  private removeArea(area: Area, toAreaId?: string): void {
    const destination = toAreaId ? this.areas.find((a) => a.id === toAreaId) : undefined;
    this.subscriptions.add(
      this.areaService.remove(area.id, toAreaId).subscribe({
        next: () => {
          this.poNotification.success(destination ? `Área "${area.area}" excluída; o que a usava foi para "${destination.area}".` : `Área "${area.area}" excluída.`);
          this.load();
        },
        // 409: a contagem estava defasada. O PO já mostra a mensagem do servidor; sem destino escolhido,
        // reconsultamos o uso e oferecemos a transferência (com destino, o servidor já moveu o que podia).
        error: (err: HttpErrorResponse) => {
          if (err?.status === 409) {
            this.load();
            if (!toAreaId) this.confirmRemoveArea(area);
          }
        },
      }),
    );
  }

  // ── Transferência ───────────────────────────────────────────────────────────

  openTransfer(area: Area, mode: TransferMode, references: Array<string> = []): void {
    this.transferArea = area;
    this.transferMode = mode;
    this.transferReferences = mode === "delete" ? references : [];
    this.transferToAreaId = "";
    this.transferWho = "all";
    this.transferUsers = [];
    this.transferSelected = [];
    this.transferDestinationOptions = this.areas
      .filter((a) => a.id !== area.id)
      .sort((a, b) => a.area.localeCompare(b.area))
      .map((a) => ({ value: a.id, label: a.tribeName ? `${a.area} — ${a.tribeName}` : `${a.area} — sem tribe` }));
    this.areaCombo.setData(this.areas.filter((a) => a.id !== area.id), this.tribes);
    this.transferPrimaryAction = {
      label: mode === "delete" ? "Transferir e excluir área" : "Transferir",
      danger: mode === "delete",
      action: () => this.confirmTransfer(),
    };
    this.transferModal?.open();
  }

  get transferTitle(): string {
    return this.transferMode === "delete" ? `Excluir área "${this.transferArea?.area ?? ""}"` : `Transferir usuários de "${this.transferArea?.area ?? ""}"`;
  }

  get transferDestination(): Area | undefined {
    return this.areas.find((a) => a.id === this.transferToAreaId);
  }

  onTransferWhoChange(who: "all" | "some"): void {
    this.transferWho = who;
    if (who === "some" && !this.transferUsers.length && this.transferArea) {
      this.loadingTransferUsers = true;
      this.subscriptions.add(
        this.usersAccessService.list({ area: [this.transferArea.id], limit: 200 }).subscribe({
          next: (page) => (this.transferUsers = page.items),
          complete: () => (this.loadingTransferUsers = false),
          error: () => (this.loadingTransferUsers = false),
        }),
      );
    }
  }

  onTransferUserSelected(row: UserAccessItem): void {
    this.transferSelected = [...new Set([...this.transferSelected, row.username])];
  }

  onTransferUserUnselected(row: UserAccessItem): void {
    this.transferSelected = this.transferSelected.filter((u) => u !== row.username);
  }

  onTransferAllSelected(): void {
    this.transferSelected = this.transferUsers.map((u) => u.username);
  }

  onTransferAllUnselected(): void {
    this.transferSelected = [];
  }

  get transferCount(): number {
    return this.transferWho === "all" ? this.transferArea?.usersCount ?? 0 : this.transferSelected.length;
  }

  confirmTransfer(): void {
    const area = this.transferArea;
    if (!area || !this.transferToAreaId) {
      this.poNotification.warning("Escolha a área de destino.");
      return;
    }
    if (this.transferWho === "some" && !this.transferSelected.length) {
      this.poNotification.warning("Escolha ao menos um usuário.");
      return;
    }

    const deleteAfter = this.transferMode === "delete" && this.transferWho === "all";
    const toAreaId = this.transferToAreaId;
    // Só referências, sem usuários: o próprio DELETE as move para o destino
    if (deleteAfter && !area.usersCount) {
      this.transferModal?.close();
      this.removeArea(area, toAreaId);
      return;
    }

    const request: Observable<TransferUsersResult> =
      this.transferWho === "all"
        ? this.userTransferService.transferAll(area.id, this.transferToAreaId)
        : this.userTransferService.transfer(this.transferToAreaId, this.transferSelected);

    this.transferring = true;
    this.transferPrimaryAction = { ...this.transferPrimaryAction, loading: true };
    this.subscriptions.add(
      request.subscribe({
        next: (result) => {
          this.poNotification.success(`${result.moved} usuário(s) transferido(s) para "${this.transferDestination?.area ?? ""}".`);
          this.transferModal?.close();
          if (deleteAfter) {
            this.removeArea(area, toAreaId);
          } else {
            this.load();
          }
        },
        complete: () => this.endTransfer(),
        error: () => this.endTransfer(),
      }),
    );
  }

  private endTransfer(): void {
    this.transferring = false;
    this.transferPrimaryAction = { ...this.transferPrimaryAction, loading: false };
  }
}
